import React, { useState, useRef, useEffect, useMemo } from 'react';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  id?: string;
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  searchable = true,
  error,
  disabled = false,
  className = '',
  name,
  id,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Reset focused index when filtered options change
  useEffect(() => {
    setFocusedIndex(0);
  }, [filteredOptions.length]);

  // Scroll focused option into view
  useEffect(() => {
    if (isOpen && listboxRef.current) {
      const focusedEl = listboxRef.current.querySelector(`[data-index="${focusedIndex}"]`);
      focusedEl?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleSelect = (optionValue: string) => {
    onChange?.(optionValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isOpen && filteredOptions.length > 0) {
        handleSelect(filteredOptions[focusedIndex].value);
      } else {
        setIsOpen(!isOpen);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        setFocusedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (isOpen) {
        setFocusedIndex(prev => Math.max(prev - 1, 0));
      }
    } else if (event.key === 'Home') {
      event.preventDefault();
      setFocusedIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setFocusedIndex(filteredOptions.length - 1);
    }
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Enter') {
      event.preventDefault();
      if (filteredOptions.length > 0) {
        handleSelect(filteredOptions[focusedIndex].value);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, 0));
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={`select-wrapper ${className}`}
      style={{ position: 'relative' }}
    >
      <input type="hidden" name={name} value={value || ''} />
      <div
        id={id}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-activedescendant={isOpen && filteredOptions.length > 0 ? `select-option-${focusedIndex}` : undefined}
        aria-invalid={!!error}
        tabIndex={disabled ? -1 : 0}
        className={`select ${error ? 'input-error' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      >
        {selectedOption?.label || <span style={{ color: 'var(--color-muted)' }}>{placeholder}</span>}
      </div>
      <div
        className="select-arrow"
        style={{
          transform: isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
          transition: 'transform 0.15s ease',
          top: '50%',
        }}
      />

      {isOpen && (
        <div
          ref={listboxRef}
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 50,
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            maxHeight: '260px',
            overflowY: 'auto',
            padding: '4px',
          }}
        >
          {searchable && (
            <div style={{ padding: 'var(--space-2)', borderBottom: '1px solid var(--line)', marginBottom: '4px' }}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search..."
                className="input"
                style={{ fontSize: 'var(--text-sm)' }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {filteredOptions.length === 0 ? (
            <div
              style={{
                padding: 'var(--space-3) var(--space-4)',
                color: 'var(--color-muted)',
                textAlign: 'center',
              }}
            >
              No options found
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                id={`select-option-${index}`}
                data-index={index}
                role="option"
                aria-selected={option.value === value}
                onClick={() => handleSelect(option.value)}
                onMouseEnter={() => setFocusedIndex(index)}
                style={{
                  padding: 'var(--space-2) var(--space-3)',
                  fontSize: 'var(--text-sm)',
                  cursor: 'pointer',
                  borderRadius: 'var(--radius-sm)',
                  backgroundColor:
                    option.value === value
                      ? 'var(--ink)'
                      : index === focusedIndex
                        ? 'var(--bg-sunk)'
                        : 'transparent',
                  color: option.value === value ? 'var(--bg)' : 'var(--ink)',
                  fontFamily: 'var(--font-body)',
                  transition: 'background-color var(--transition-fast)',
                }}
              >
                {option.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
